import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Member } from "../../features/members/models/Member";

interface AddOneOffMemberPickerProps {
  members: Member[];
  /** Members already on the roster or already added one-off. */
  excludedMemberIds: string[];
  onAdd: (memberId: string) => void;
  /** Trigger label override (defaults to the attendance one-off wording). */
  label?: string;
}

/**
 * Small searchable dropdown to add a member to a single occurrence's attendance
 * (a one-off visit). The plan roster stays untouched.
 */
const AddOneOffMemberPicker: React.FC<AddOneOffMemberPickerProps> = ({
  members,
  excludedMemberIds,
  onAdd,
  label,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Close the dropdown when clicking anywhere outside it.
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const candidates = useMemo(() => {
    const excluded = new Set(excludedMemberIds);
    const needle = search.trim().toLowerCase();
    return members
      .filter((m) => !excluded.has(m.id))
      .filter((m) => {
        if (!needle) return true;
        return `${m.firstName} ${m.lastName}`.toLowerCase().includes(needle);
      })
      .slice(0, 8);
  }, [members, excludedMemberIds, search]);

  const handleAdd = (memberId: string) => {
    onAdd(memberId);
    setSearch("");
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-40 hover:bg-grey-95 dark:text-primary-90 dark:hover:bg-primary-20"
      >
        <Plus size={14} />
        {label ?? t("sessions.attendance.addOneOffMember")}
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-64 rounded-xl border border-grey-90 bg-white p-2 shadow-lg dark:border-primary-20 dark:bg-primary-10">
          <input
            autoFocus
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("sessions.attendance.searchMembers")}
            className="input w-full text-sm"
          />
          <div className="mt-2 max-h-48 overflow-y-auto">
            {candidates.length === 0 ? (
              <p className="px-2 py-1 text-sm text-grey-40 dark:text-grey-60">
                {t("sessions.attendance.noMatchingMembers")}
              </p>
            ) : (
              candidates.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => handleAdd(member.id)}
                  className="block w-full rounded-lg px-2 py-1.5 text-left text-sm text-grey-10 hover:bg-grey-95 dark:text-grey-90 dark:hover:bg-primary-20"
                >
                  {`${member.firstName} ${member.lastName}`.trim()}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOneOffMemberPicker;
